/**
 * Normalise paginated freight index API: `data.list` + `data.meta`, or totals on `data`.
 * @param {unknown} res - Full JSON body from `makeAdminJsonRequest` (usually `{ data: { list, meta } }`).
 * @param {number} [pageSize=25] - Used to derive `total_pages` when the API omits it but sends `total_count`.
 * @returns {{ list: unknown[], totalCount: number, totalPages: number, recordsFiltered: number, recordsTotal: number }}
 */
export function parseFreightIndexResponse(res, pageSize = 25) {
  const top = res != null && typeof res === "object" && !Array.isArray(res) ? res : {};
  let wrap =
    top.data != null && typeof top.data === "object" && !Array.isArray(top.data) ? top.data : top;

  /** `{ data: { data: { list, meta } } }` or similar double envelope */
  if (
    wrap &&
    typeof wrap === "object" &&
    !Array.isArray(wrap) &&
    wrap.list == null &&
    wrap.rows == null &&
    wrap.items == null &&
    wrap.users == null &&
    wrap.data != null &&
    typeof wrap.data === "object" &&
    !Array.isArray(wrap.data)
  ) {
    const inner = wrap.data;
    if (
      Array.isArray(inner.list) ||
      Array.isArray(inner.rows) ||
      Array.isArray(inner.items) ||
      Array.isArray(inner.users)
    ) {
      wrap = inner;
    }
  }

  const data = wrap && typeof wrap === "object" && !Array.isArray(wrap) ? wrap : {};
  const list = Array.isArray(data.list)
    ? data.list
    : Array.isArray(data.rows)
      ? data.rows
      : Array.isArray(data.items)
        ? data.items
        : Array.isArray(data.users)
          ? data.users
          : Array.isArray(wrap)
            ? wrap
            : [];

  const meta = data.meta && typeof data.meta === "object" ? data.meta : null;

  const totalFromMeta =
    meta != null && meta.total_count != null
      ? Number(meta.total_count) || 0
      : meta != null && meta.total != null
        ? Number(meta.total) || 0
        : null;
  const totalFromData =
    data.total_count != null
      ? Number(data.total_count) || 0
      : data.totalCount != null
        ? Number(data.totalCount) || 0
        : data.total != null
          ? Number(data.total) || 0
          : null;

  const totalCount =
    totalFromMeta != null && totalFromMeta > 0
      ? totalFromMeta
      : totalFromData != null && totalFromData > 0
        ? totalFromData
        : list.length;

  const pagesFromMeta =
    meta != null && meta.total_pages != null ? Math.max(1, Number(meta.total_pages) || 1) : null;
  const pagesFromData =
    data.total_pages != null
      ? Math.max(1, Number(data.total_pages) || 1)
      : data.totalPages != null
        ? Math.max(1, Number(data.totalPages) || 1)
        : null;

  const totalPages =
    pagesFromMeta != null
      ? pagesFromMeta
      : pagesFromData != null
        ? pagesFromData
        : totalCount > 0
          ? Math.max(1, Math.ceil(totalCount / pageSize))
          : 1;

  const filteredFromMeta =
    meta != null && meta.records_filtered != null
      ? Number(meta.records_filtered)
      : meta != null && meta.filtered_count != null
        ? Number(meta.filtered_count)
        : meta != null && meta.recordsFiltered != null
          ? Number(meta.recordsFiltered)
          : meta != null && meta.filtered != null
            ? Number(meta.filtered)
            : null;
  const recordsFiltered =
    filteredFromMeta != null && !Number.isNaN(filteredFromMeta) && filteredFromMeta >= 0
      ? filteredFromMeta
      : totalCount;

  const grandFromMeta =
    meta != null && meta.grand_total != null
      ? Number(meta.grand_total)
      : meta != null && meta.total_unfiltered != null
        ? Number(meta.total_unfiltered)
        : null;
  const recordsTotal =
    grandFromMeta != null && !Number.isNaN(grandFromMeta) && grandFromMeta >= 0
      ? grandFromMeta
      : totalCount;

  return { list, totalCount, totalPages, recordsFiltered, recordsTotal };
}
